import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../api/axiosConfig';
import ImageViewerModal from '../components/ImageViewerModal';
import { STATE, stateOf, fmtTime, fmtLateness } from '../utils/slotState';
import { formatDescription, describeCounts } from '../utils/reportDescription';
import { fmtDayRange, fmtDayWithWeekday } from '../utils/formatDate';

const stopName = (stop) =>
    stop.section?.name || `${stop.facility?.name ?? ''} 전체`;

/**
 * 회차 하나가 남긴 기록을 통째로 보는 화면.
 *
 * 예전에는 주간 현황의 칸을 열고 기록을 하나 누르면 화면이 갈아치워졌고,
 * 돌아오면 칸이 닫혀 있었다. 열 곳을 돈 순회를 확인하려면 스무 번을 오갔다.
 * 회차가 이 시스템의 단위이므로 확인하는 화면도 회차를 통째로 낸다.
 *
 * 사진은 펼친 기록만 받아 온다. 서명한 주소를 만드는 데 한 장마다 값이 들고,
 * 열 건이면 들어오자마자 그 값을 다 치르게 된다. 글은 이미 받아 두었으므로
 * 펼치는 순간 읽을 것은 있고, 사진만 뒤따라 온다.
 */
const RoundDetailPage = () => {
    const { slotId } = useParams();
    const navigate = useNavigate();

    const [round, setRound] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const [open, setOpen] = useState({});
    /** 기록 id -> 서명된 사진 주소. 한 번 받으면 다시 받지 않는다. */
    const [photos, setPhotos] = useState({});
    const [viewer, setViewer] = useState(null);

    useEffect(() => {
        let alive = true;
        setLoading(true);
        api
            .get(`/inspection-plans/slots/${slotId}`)
            .then((res) => alive && setRound(res.data))
            .catch((err) => {
                console.error('Failed to load round', err);
                if (alive) setError('회차를 불러오지 못했습니다.');
            })
            .finally(() => alive && setLoading(false));
        return () => {
            alive = false;
        };
    }, [slotId]);

    const loadPhotos = useCallback((reportId) => {
        setPhotos((prev) => (reportId in prev ? prev : { ...prev, [reportId]: null }));
        api
            .get(`/reports/${reportId}`)
            .then((res) =>
                setPhotos((prev) => ({ ...prev, [reportId]: res.data?.photoUrls || [] })),
            )
            .catch((err) => {
                console.error('Failed to load photos', err);
                setPhotos((prev) => ({ ...prev, [reportId]: [] }));
            });
    }, []);

    const toggle = (stop) => {
        const wasOpen = open[stop.id];
        setOpen((prev) => ({ ...prev, [stop.id]: !wasOpen }));
        if (!wasOpen && stop.report?.photoCount > 0 && !(stop.report.id in photos)) {
            loadPhotos(stop.report.id);
        }
    };

    const withRecord = (round?.stops || []).filter((s) => s.report);
    const allOpen = withRecord.length > 0 && withRecord.every((s) => open[s.id]);

    const toggleAll = () => {
        if (allOpen) {
            setOpen({});
            return;
        }
        const next = {};
        for (const s of withRecord) {
            next[s.id] = true;
            if (s.report.photoCount > 0 && !(s.report.id in photos)) loadPhotos(s.report.id);
        }
        setOpen(next);
    };

    if (loading) {
        return (
            <div className="text-center p-5">
                <div className="spinner-border text-primary" role="status"></div>
            </div>
        );
    }

    if (error || !round) {
        return (
            <div className="pb-5">
                <div className="alert alert-warning">{error || '회차를 찾을 수 없습니다.'}</div>
                <button className="btn btn-sm btn-white border" onClick={() => navigate(-1)}>
                    돌아가기
                </button>
            </div>
        );
    }

    const s = stateOf(round);

    return (
        <div className="pb-5">
            <style>{`
                .round-photo img {
                    width: 100%; aspect-ratio: 1; object-fit: cover;
                    border-radius: 10px; border: 1px solid #eee; cursor: pointer;
                }
                .round-stop { border-top: 1px solid #f1f3f5; }
                .round-stop:first-child { border-top: 0; }
                .legend-dot {
                    width: 16px; height: 16px; border-radius: 50%;
                    display: flex; align-items: center; justify-content: center;
                    font-size: 0.6rem; font-weight: 700;
                }
            `}</style>

            <div className="d-flex align-items-center gap-2 mb-3">
                <button className="btn border-0 p-0" onClick={() => navigate(-1)}>
                    <i className="bi bi-chevron-left fs-5"></i>
                </button>
                <h5 className="mb-0 fw-bold">회차 기록</h5>
            </div>

            {/* 머리말: 어느 계획의 언제 회차이고 어땠는가. 주간 현황의 칸에서
                보던 것과 같은 말을 쓴다. */}
            <div className="bg-white rounded-4 border p-3 mb-3">
                <div className="fw-bold mb-1">{round.planName}</div>
                <div className="text-muted small mb-2">
                    {round.date ? fmtDayWithWeekday(round.date) : `${fmtDayRange(round.periodStart, round.periodEnd)} 중`}
                    {round.labelText ? ` · ${round.labelText}` : ''}
                </div>
                <div className="d-flex align-items-center gap-2 flex-wrap">
                    <span className="legend-dot" style={{ background: s.bg, color: s.color }}>
                        {s.symbol}
                    </span>
                    <span className="fw-bold small">{s.text}</span>
                    {round.completedAt && (
                        <span className="text-muted small">· 완료 {fmtTime(round.completedAt)}</span>
                    )}
                    {round.lateMinutes > 0 && (
                        <span className="small" style={{ color: STATE.LATE.color }}>
                            · {fmtLateness(round.lateMinutes)}
                        </span>
                    )}
                    <span className="text-muted small ms-auto">
                        {round.route?.name ? `${round.route.name} · ` : ''}
                        {round.progress.done}/{round.progress.total}곳
                    </span>
                </div>
            </div>

            {withRecord.length > 1 && (
                <div className="d-flex justify-content-end mb-2">
                    <button
                        className="btn btn-sm btn-white border rounded-pill px-3"
                        style={{ fontSize: '0.78rem' }}
                        onClick={toggleAll}
                    >
                        <i className={`bi bi-chevron-${allOpen ? 'contract' : 'expand'} me-1`}></i>
                        {allOpen ? '모두 접기' : '모두 펼치기'}
                    </button>
                </div>
            )}

            <div className="bg-white rounded-4 border">
                {(round.stops || []).map((stop) => {
                    const record = stop.report;
                    const expanded = open[stop.id];
                    const urls = record ? photos[record.id] : null;

                    return (
                        <div key={stop.id} className="round-stop">
                            <button
                                className="btn w-100 text-start d-flex align-items-center gap-2 p-3"
                                onClick={() => record && toggle(stop)}
                                disabled={!record}
                                style={{ opacity: record ? 1 : 0.65 }}
                            >
                                <span style={{ color: stop.completedAt ? STATE.ON_TIME.color : '#d1d5db' }}>
                                    {stop.completedAt ? '●' : '○'}
                                </span>
                                <span className="text-truncate">
                                    <span className="fw-bold" style={{ fontSize: '0.9rem' }}>
                                        {stopName(stop)}
                                    </span>
                                    <span className="text-muted d-block" style={{ fontSize: '0.75rem' }}>
                                        {/* 안 들른 곳도 줄로 남는다. 무엇이 빠졌는지가 회차의
                                            절반이다. 남이 올린 기록은 다녀갔다는 사실만 남긴다. */}
                                        {stop.completedAt
                                            ? [
                                                fmtTime(stop.completedAt),
                                                stop.restricted ? '작성자만 볼 수 있습니다' : null,
                                                record ? describeCounts(record.description) : null,
                                                record?.photoCount ? `사진 ${record.photoCount}장` : null,
                                            ].filter(Boolean).join(' · ')
                                            : '들르지 않음'}
                                    </span>
                                </span>
                                {record && (
                                    <i
                                        className={`bi bi-chevron-${expanded ? 'up' : 'down'} ms-auto text-muted`}
                                        style={{ fontSize: '0.8rem' }}
                                    ></i>
                                )}
                            </button>

                            {record && expanded && (
                                <div className="px-3 pb-3">
                                    <div className="bg-light rounded-3 p-3" style={{ lineHeight: 1.6, fontSize: '0.85rem' }}>
                                        {formatDescription(record.description)}
                                    </div>

                                    {record.photoCount > 0 && (
                                        <div className="mt-2">
                                            {urls === null || urls === undefined ? (
                                                <div className="text-muted small">사진 불러오는 중…</div>
                                            ) : (
                                                <div className="row g-2 round-photo">
                                                    {urls.map((url, i) => (
                                                        <div key={i} className="col-3 col-md-2">
                                                            <img
                                                                src={url}
                                                                alt={`${stopName(stop)} ${i + 1}`}
                                                                onClick={() => setViewer({ images: urls, index: i })}
                                                            />
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    )}

                                    <div className="d-flex align-items-center gap-2 mt-2">
                                        <span className="text-muted" style={{ fontSize: '0.75rem' }}>
                                            {record.reporterName || '알 수 없음'} · {fmtTime(record.createdAt)}
                                        </span>
                                        <button
                                            className="btn btn-sm border-0 p-0 text-primary ms-auto"
                                            style={{ fontSize: '0.78rem' }}
                                            onClick={() => navigate(`/inspections/${record.id}`)}
                                        >
                                            기록 하나만 보기
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>

            <ImageViewerModal
                show={Boolean(viewer)}
                onHide={() => setViewer(null)}
                images={viewer?.images || []}
                initialIndex={viewer?.index || 0}
            />
        </div>
    );
};

export default RoundDetailPage;
